import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Users, FileText, Building2, Loader2, Trash2, Eye, Shield, CheckCircle, 
  XCircle, Award, Newspaper, Plus, RefreshCw, Star, ExternalLink, Ban, 
  Unlock, CreditCard, Image, TrendingUp, DollarSign, Search, ArrowLeft,
  X, MapPin, Phone, Mail, Globe, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [offers, setOffers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');
  const [generatingNews, setGeneratingNews] = useState(false);
  
  // Search
  const [companySearch, setCompanySearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [offerSearch, setOfferSearch] = useState('');
  
  // Company detail view
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyUser, setCompanyUser] = useState(null);
  const [companyReviews, setCompanyReviews] = useState([]);
  const [companyOffers, setCompanyOffers] = useState([]);
  const [loadingCompany, setLoadingCompany] = useState(false);
  
  // Individual user detail view
  const [selectedUser, setSelectedUser] = useState(null);
  const [userOffers, setUserOffers] = useState([]);
  const [userReviews, setUserReviews] = useState([]);
  const [loadingUser, setLoadingUser] = useState(false);
  
  // Forms
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [newArticle, setNewArticle] = useState({ title: '', summary: '', category: 'news', source: 'Admin', source_url: '' });
  const [subscriptionDays, setSubscriptionDays] = useState(30);
  
  // Chats
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  
  // Reviews management
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({ company_id: '', rating: 5, user_name: '', comment: '' });
  
  // Admin messaging
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState(null);
  const [messageContent, setMessageContent] = useState('');
  const [allUsers, setAllUsers] = useState([]);

  const isAdmin = user?.is_admin;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      navigate('/');
      toast.error('Brak uprawnień administratora');
      return;
    }
    fetchData();
  }, [isAuthenticated, isAdmin, navigate]);

  const fetchData = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      const [statsRes, usersRes, offersRes, companiesRes, reviewsRes, newsRes, chatsRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { headers }),
        fetch(`${API}/admin/users`, { headers }),
        fetch(`${API}/admin/offers`, { headers }),
        fetch(`${API}/admin/companies`, { headers }),
        fetch(`${API}/admin/reviews`, { headers }),
        fetch(`${API}/admin/news`, { headers }),
        fetch(`${API}/admin/chats`, { headers }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) { const d = await usersRes.json(); setUsers(d.users || []); }
      if (offersRes.ok) { const d = await offersRes.json(); setOffers(d.offers || []); }
      if (companiesRes.ok) { const d = await companiesRes.json(); setCompanies(d.companies || []); }
      if (reviewsRes.ok) { const d = await reviewsRes.json(); setReviews(d.reviews || []); }
      if (newsRes.ok) { const d = await newsRes.json(); setNews(d.news || []); }
      if (chatsRes.ok) { const d = await chatsRes.json(); setChats(d.chats || []); }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const apiCall = async (url, method = 'POST', body = null) => {
    const res = await fetch(`${API}${url}`, {
      method,
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : null
    });
    return res;
  };

  // Open company detail view
  const openCompanyDetail = async (company) => {
    setLoadingCompany(true);
    setSelectedCompany(company);
    
    // Find user for this company
    const compUser = users.find(u => u.user_id === company.user_id);
    setCompanyUser(compUser);
    
    // Get reviews for this company
    const compReviews = reviews.filter(r => r.company_id === company.company_id);
    setCompanyReviews(compReviews);
    
    // Get offers for this company's user
    const compOffers = offers.filter(o => o.user_id === company.user_id);
    setCompanyOffers(compOffers);
    
    setLoadingCompany(false);
  };

  const closeCompanyDetail = () => {
    setSelectedCompany(null);
    setCompanyUser(null);
    setCompanyReviews([]);
    setCompanyOffers([]);
  };

  // Open individual user detail view
  const openUserDetail = async (userItem) => {
    setLoadingUser(true);
    setSelectedUser(userItem);
    
    // Get offers posted by this user
    const userOffersData = offers.filter(o => o.user_id === userItem.user_id);
    setUserOffers(userOffersData);
    
    // Get reviews written by this user
    const userReviewsData = reviews.filter(r => r.user_id === userItem.user_id);
    setUserReviews(userReviewsData);
    
    setLoadingUser(false);
  };

  const closeUserDetail = () => {
    setSelectedUser(null);
    setUserOffers([]);
    setUserReviews([]);
  };

  // User actions
  const deleteUser = async (userId) => {
    if (!window.confirm('Czy na pewno chcesz USUNĄĆ tego użytkownika? Ta operacja jest nieodwracalna.')) return;
    const res = await apiCall(`/admin/users/${userId}`, 'DELETE');
    if (res.ok) { toast.success('Użytkownik usunięty'); fetchData(); closeCompanyDetail(); }
    else toast.error('Błąd podczas usuwania');
  };

  const blockUser = async (userId, userName) => {
    const reason = window.prompt(`Podaj powód blokady użytkownika "${userName}":`, 'Naruszenie regulaminu');
    if (reason === null) return;
    const res = await apiCall(`/admin/users/${userId}/block`, 'POST', { reason });
    if (res.ok) { toast.success('Użytkownik zablokowany'); fetchData(); }
    else toast.error('Błąd podczas blokowania');
  };

  const unblockUser = async (userId) => {
    const res = await apiCall(`/admin/users/${userId}/unblock`, 'POST');
    if (res.ok) { toast.success('Użytkownik odblokowany'); fetchData(); }
    else toast.error('Błąd podczas odblokowywania');
  };

  const grantSubscription = async (userId, days) => {
    const res = await apiCall(`/admin/users/${userId}/subscription/grant`, 'POST', { days });
    if (res.ok) { 
      toast.success(`Subskrypcja nadana na ${days} dni`); 
      fetchData();
      // Update local state
      if (companyUser && companyUser.user_id === userId) {
        setCompanyUser(prev => ({
          ...prev,
          subscription: { active: true, expires_at: new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString() }
        }));
      }
    }
    else toast.error('Błąd podczas nadawania subskrypcji');
  };

  const revokeSubscription = async (userId) => {
    if (!window.confirm('Czy na pewno chcesz ODEBRAĆ subskrypcję?')) return;
    const res = await apiCall(`/admin/users/${userId}/subscription/revoke`, 'POST');
    if (res.ok) { 
      toast.success('Subskrypcja odebrana'); 
      fetchData();
      if (companyUser && companyUser.user_id === userId) {
        setCompanyUser(prev => ({
          ...prev,
          subscription: { active: false }
        }));
      }
    }
    else toast.error('Błąd podczas odbierania subskrypcji');
  };

  // Promotion actions
  const setCompanyPromotion = async (companyId, active) => {
    const action = active ? 'nadać' : 'odebrać';
    if (!window.confirm(`Czy na pewno chcesz ${action} promowanie tej firmie?`)) return;
    const res = await apiCall(`/admin/companies/${companyId}/promotion`, 'PUT', { active });
    if (res.ok) { 
      toast.success(active ? 'Promowanie nadane na 7 dni' : 'Promowanie odebrane'); 
      fetchData();
      if (selectedCompany && selectedCompany.company_id === companyId) {
        setSelectedCompany(prev => ({ 
          ...prev, 
          is_promoted: active,
          promoted_until: active ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() : null
        }));
      }
    }
    else toast.error(`Błąd podczas ${action === 'nadać' ? 'nadawania' : 'odbierania'} promowania`);
  };

  // Review actions
  const deleteReview = async (reviewId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę opinię?')) return;
    const res = await apiCall(`/admin/reviews/${reviewId}`, 'DELETE');
    if (res.ok) { 
      toast.success('Opinia usunięta'); 
      fetchData();
      setCompanyReviews(prev => prev.filter(r => r.review_id !== reviewId));
    }
    else toast.error('Błąd podczas usuwania');
  };

  const createReview = async () => {
    if (!reviewForm.company_id || !reviewForm.rating) {
      toast.error('Wybierz firmę i ocenę');
      return;
    }
    const res = await apiCall('/admin/reviews', 'POST', reviewForm);
    if (res.ok) {
      toast.success('Opinia dodana');
      setShowReviewForm(false);
      setReviewForm({ company_id: '', rating: 5, user_name: '', comment: '' });
      fetchData();
    } else {
      toast.error('Błąd podczas dodawania opinii');
    }
  };

  const updateReview = async () => {
    if (!editingReview) return;
    const res = await apiCall(`/admin/reviews/${editingReview.review_id}`, 'PUT', {
      rating: reviewForm.rating,
      user_name: reviewForm.user_name,
      comment: reviewForm.comment
    });
    if (res.ok) {
      toast.success('Opinia zaktualizowana');
      setEditingReview(null);
      setShowReviewForm(false);
      setReviewForm({ company_id: '', rating: 5, user_name: '', comment: '' });
      fetchData();
    } else {
      toast.error('Błąd podczas aktualizacji opinii');
    }
  };

  const startEditReview = (review) => {
    setEditingReview(review);
    setReviewForm({
      company_id: review.company_id,
      rating: review.rating,
      user_name: review.user_name || '',
      comment: review.comment || ''
    });
    setShowReviewForm(true);
  };

  // Admin messaging
  const sendAdminMessage = async () => {
    if (!messageRecipient || !messageContent.trim()) {
      toast.error('Wybierz odbiorcę i wpisz treść');
      return;
    }
    const res = await apiCall('/admin/messages/send', 'POST', {
      recipient_id: messageRecipient.id || messageRecipient.user_id,
      content: messageContent.trim()
    });
    if (res.ok) {
      toast.success(`Wiadomość wysłana do: ${messageRecipient.name}`);
      setShowMessageForm(false);
      setMessageRecipient(null);
      setMessageContent('');
    } else {
      toast.error('Błąd podczas wysyłania wiadomości');
    }
  };

  const openMessageForm = (userItem) => {
    setMessageRecipient(userItem);
    setMessageContent('');
    setShowMessageForm(true);
  };

  // Offer actions
  const deleteOffer = async (offerId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę ofertę?')) return;
    const res = await apiCall(`/admin/offers/${offerId}`, 'DELETE');
    if (res.ok) { toast.success('Oferta usunięta'); fetchData(); }
    else toast.error('Błąd podczas usuwania');
  };

  const changeOfferStatus = async (offerId, status) => {
    const res = await apiCall(`/admin/offers/${offerId}/status`, 'PUT', { status });
    if (res.ok) { toast.success(`Status zmieniony na: ${status}`); fetchData(); }
    else toast.error('Błąd podczas zmiany statusu');
  };

  // Company actions
  const deleteCompanyLogo = async (companyId) => {
    if (!window.confirm('Usunąć logo firmy?')) return;
    const res = await apiCall(`/admin/companies/${companyId}/logo`, 'DELETE');
    if (res.ok) { 
      toast.success('Logo usunięte'); 
      fetchData();
      if (selectedCompany && selectedCompany.company_id === companyId) {
        setSelectedCompany(prev => ({ ...prev, logo_url: null }));
      }
    }
    else toast.error('Błąd');
  };

  const deleteGalleryImage = async (companyId, index) => {
    if (!window.confirm('Usunąć to zdjęcie z galerii?')) return;
    const res = await apiCall(`/admin/companies/${companyId}/gallery/${index}`, 'DELETE');
    if (res.ok) { 
      toast.success('Zdjęcie usunięte'); 
      fetchData();
      if (selectedCompany && selectedCompany.company_id === companyId) {
        setSelectedCompany(prev => ({
          ...prev,
          gallery: prev.gallery.filter((_, i) => i !== index)
        }));
      }
    }
    else toast.error('Błąd');
  };

  // News actions
  const addNews = async () => {
    if (!newArticle.title || !newArticle.summary) { toast.error('Wypełnij tytuł i treść'); return; }
    const res = await apiCall('/admin/news', 'POST', newArticle);
    if (res.ok) {
      toast.success('Artykuł dodany');
      setShowNewsForm(false);
      setNewArticle({ title: '', summary: '', category: 'news', source: 'Admin', source_url: '' });
      fetchData();
    }
  };

  const deleteNews = async (newsId) => {
    if (!window.confirm('Usunąć artykuł?')) return;
    const res = await apiCall(`/admin/news/${newsId}`, 'DELETE');
    if (res.ok) { toast.success('Artykuł usunięty'); fetchData(); }
  };

  const generateNews = async () => {
    setGeneratingNews(true);
    const res = await apiCall('/news/generate', 'POST');
    setGeneratingNews(false);
    if (res.ok) { toast.success('Wiadomości wygenerowane'); fetchData(); }
    else toast.error('Błąd generowania');
  };

  // Chat functions
  const openChatMessages = async (chat) => {
    setLoadingChat(true);
    setSelectedChat(chat);
    try {
      const res = await fetch(`${API}/admin/chats/${chat.conversation_id}/messages`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        setChatMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error loading chat messages:', error);
      toast.error('Błąd ładowania wiadomości');
    } finally {
      setLoadingChat(false);
    }
  };

  const closeChatMessages = () => {
    setSelectedChat(null);
    setChatMessages([]);
  };

  // Filter functions
  const filteredCompanies = companies.filter(c => 
    c.name?.toLowerCase().includes(companySearch.toLowerCase()) ||
    c.location?.toLowerCase().includes(companySearch.toLowerCase())
  );

  const filteredUsers = users.filter(u =>
    u.user_type === 'individual' && (
      u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email?.toLowerCase().includes(userSearch.toLowerCase())
    )
  );

  const filteredOffers = offers.filter(o =>
    o.title?.toLowerCase().includes(offerSearch.toLowerCase()) ||
    o.location?.toLowerCase().includes(offerSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  // Company Detail View
  if (selectedCompany) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="admin-company-detail">
        <div className="bg-slate-900 py-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Button variant="ghost" onClick={closeCompanyDetail} className="text-white mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />Powrót do listy
            </Button>
            <div className="flex items-center gap-4">
              {selectedCompany.logo_url ? (
                <img src={selectedCompany.logo_url} alt={selectedCompany.name} className="w-16 h-16 rounded-lg object-cover" />
              ) : (
                <div className="w-16 h-16 bg-slate-700 rounded-lg flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-slate-400" />
                </div>
              )}
              <div>
                <h1 className="font-heading font-bold text-2xl text-white flex items-center gap-2">
                  {selectedCompany.name}
                  {selectedCompany.is_promoted && <Badge className="bg-orange-500">Promowana</Badge>}
                </h1>
                <p className="text-slate-400">{selectedCompany.location}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          {/* User & Subscription Management */}
          {companyUser && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Właściciel konta i subskrypcja
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="font-medium">{companyUser.name}</p>
                    <p className="text-sm text-slate-500">{companyUser.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {companyUser.is_blocked && <Badge variant="destructive">Zablokowany</Badge>}
                      {companyUser.subscription?.active ? (
                        <Badge className="bg-green-500">Subskrypcja aktywna</Badge>
                      ) : (
                        <Badge variant="secondary">Brak subskrypcji</Badge>
                      )}
                    </div>
                    {companyUser.subscription?.expires_at && (
                      <p className="text-xs text-slate-500 mt-1">
                        Wygasa: {new Date(companyUser.subscription.expires_at).toLocaleDateString('pl-PL')}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {/* Subscription controls */}
                    {companyUser.subscription?.active ? (
                      <Button variant="outline" onClick={() => revokeSubscription(companyUser.user_id)} className="text-red-600">
                        <XCircle className="w-4 h-4 mr-2" />Odbierz subskrypcję
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Select value={String(subscriptionDays)} onValueChange={(v) => setSubscriptionDays(Number(v))}>
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="7">7 dni</SelectItem>
                            <SelectItem value="30">30 dni</SelectItem>
                            <SelectItem value="90">90 dni</SelectItem>
                            <SelectItem value="365">1 rok</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button onClick={() => grantSubscription(companyUser.user_id, subscriptionDays)} className="bg-green-500 hover:bg-green-600">
                          <CheckCircle className="w-4 h-4 mr-2" />Nadaj subskrypcję
                        </Button>
                      </div>
                    )}
                    
                    {/* Block/Unblock */}
                    {companyUser.is_blocked ? (
                      <Button variant="outline" onClick={() => unblockUser(companyUser.user_id)}>
                        <Unlock className="w-4 h-4 mr-2" />Odblokuj
                      </Button>
                    ) : (
                      <Button variant="outline" onClick={() => blockUser(companyUser.user_id, companyUser.name)} className="text-orange-600">
                        <Ban className="w-4 h-4 mr-2" />Zablokuj
                      </Button>
                    )}
                    
                    <Button variant="destructive" onClick={() => deleteUser(companyUser.user_id)}>
                      <Trash2 className="w-4 h-4 mr-2" />Usuń konto
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Admin Actions Card - zawsze widoczne dla selectedCompany */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-500" />
                Akcje administratora
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Promotion controls */}
              <div>
                <p className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4 text-orange-500" />
                  Promowanie firmy
                </p>
                <div className="flex items-center gap-3">
                  {selectedCompany.is_promoted ? (
                    <>
                      <Badge className="bg-orange-500">Promowana</Badge>
                      {selectedCompany.promoted_until && (
                        <span className="text-sm text-slate-500">
                          do {new Date(selectedCompany.promoted_until).toLocaleDateString('pl-PL')}
                        </span>
                      )}
                      <Button variant="outline" onClick={() => setCompanyPromotion(selectedCompany.company_id, false)} className="text-red-600">
                        <XCircle className="w-4 h-4 mr-2" />Odbierz promowanie
                      </Button>
                    </>
                  ) : (
                    <>
                      <Badge variant="secondary">Nie promowana</Badge>
                      <Button onClick={() => setCompanyPromotion(selectedCompany.company_id, true)} className="bg-orange-500 hover:bg-orange-600">
                        <Award className="w-4 h-4 mr-2" />Nadaj promowanie (7 dni)
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Review and Message actions */}
              <div className="pt-4 border-t">
                <p className="text-sm font-medium mb-3">Dodaj opinię</p>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="text-sm text-slate-500">Ocena:</span>
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setReviewForm(prev => ({ ...prev, company_id: selectedCompany.company_id, rating: star }))}
                      className="focus:outline-none"
                    >
                      <Star 
                        className={`w-7 h-7 ${star <= (reviewForm.company_id === selectedCompany.company_id ? reviewForm.rating : 0) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300 hover:text-yellow-300'}`} 
                      />
                    </button>
                  ))}
                  <Button 
                    size="sm" 
                    onClick={createReview} 
                    disabled={reviewForm.company_id !== selectedCompany.company_id || !reviewForm.rating}
                    className="bg-green-500 hover:bg-green-600 ml-2"
                  >
                    Dodaj opinię
                  </Button>
                </div>
                
                <p className="text-sm font-medium mb-3 mt-4">Wyślij wiadomość</p>
                {companyUser ? (
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Treść wiadomości..." 
                      value={messageRecipient?.id === companyUser.user_id ? messageContent : ''}
                      onChange={(e) => {
                        setMessageRecipient(companyUser);
                        setMessageContent(e.target.value);
                      }}
                      className="flex-1"
                    />
                    <Button 
                      onClick={sendAdminMessage} 
                      disabled={!messageContent.trim() || messageRecipient?.id !== companyUser.user_id}
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      Wyślij
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">Brak przypisanego właściciela firmy</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Company Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Dane firmy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  {selectedCompany.location && (
                    <p className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-slate-400" />{selectedCompany.location}
                    </p>
                  )}
                  {selectedCompany.phone && (
                    <p className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-slate-400" />{selectedCompany.phone}
                    </p>
                  )}
                  {selectedCompany.email && (
                    <p className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-slate-400" />{selectedCompany.email}
                    </p>
                  )}
                  {selectedCompany.website && (
                    <p className="flex items-center gap-2 text-sm">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <a 
                        href={selectedCompany.website.startsWith('http') ? selectedCompany.website : `https://${selectedCompany.website}`} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-blue-600 hover:underline"
                      >
                        {selectedCompany.website}
                      </a>
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-slate-500">Usługi:</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedCompany.services?.map((s, i) => (
                      <Badge key={i} variant="outline">{s}</Badge>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Star className="w-4 h-4 text-yellow-500" />
                    <span className="font-medium">{selectedCompany.rating || 0}</span>
                    <span className="text-sm text-slate-500">({selectedCompany.review_count || 0} opinii)</span>
                  </div>
                </div>
              </div>
              
              {selectedCompany.description && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-600">{selectedCompany.description}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Logo & Gallery */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5" />
                Logo i galeria
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {selectedCompany.logo_url && (
                  <div className="relative group">
                    <img src={selectedCompany.logo_url} alt="Logo" className="w-24 h-24 rounded-lg object-cover" />
                    <Button
                      size="sm"
                      variant="destructive"
                      className="absolute -top-2 -right-2 w-6 h-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => deleteCompanyLogo(selectedCompany.company_id)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                    <p className="text-xs text-center mt-1 text-slate-500">Logo</p>
                  </div>
                )}
                
                {selectedCompany.gallery?.map((img, idx) => (
                  <div key={idx} className="relative group">
                    <img src={img} alt={`Galeria ${idx + 1}`} className="w-24 h-24 rounded-lg object-cover" />
                    <Button
                      size="sm"
                      variant="destructive"
                      className="absolute -top-2 -right-2 w-6 h-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => deleteGalleryImage(selectedCompany.company_id, idx)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                
                {!selectedCompany.logo_url && (!selectedCompany.gallery || selectedCompany.gallery.length === 0) && (
                  <p className="text-slate-500">Brak zdjęć</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Reviews */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5" />
                Opinie ({companyReviews.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {companyReviews.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Brak opinii dla tej firmy</p>
              ) : (
                <div className="space-y-4">
                  {companyReviews.map((r) => (
                    <div key={r.review_id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{r.user_name || 'Użytkownik'}</span>
                            <div className="flex text-yellow-500">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className={`w-3 h-3 ${i < r.rating ? 'fill-current' : ''}`} />
                              ))}
                            </div>
                            <span className="text-xs text-slate-400">
                              {r.timestamp ? new Date(r.timestamp).toLocaleDateString('pl-PL') : 
                               r.created_at ? new Date(r.created_at).toLocaleDateString('pl-PL') : ''}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600">{r.comment}</p>
                          {r.company_reply && (
                            <div className="mt-2 pl-4 border-l-2 border-blue-200">
                              <p className="text-xs text-blue-600 font-medium">Odpowiedź firmy:</p>
                              <p className="text-sm text-slate-500">{r.company_reply}</p>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => startEditReview(r)} className="text-blue-500">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteReview(r.review_id)} className="text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Company's Offers */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Oferty firmy ({companyOffers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {companyOffers.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Brak ofert</p>
              ) : (
                <div className="space-y-3">
                  {companyOffers.map((o) => (
                    <div key={o.offer_id} className="border rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium">{o.title}</p>
                        <p className="text-sm text-slate-500">{o.location} • {o.category}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={o.status === 'active' ? 'default' : o.status === 'blocked' ? 'destructive' : 'secondary'}>
                          {o.status}
                        </Badge>
                        <Select value={o.status} onValueChange={(v) => changeOfferStatus(o.offer_id, v)}>
                          <SelectTrigger className="w-28 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Aktywna</SelectItem>
                            <SelectItem value="closed">Zamknięta</SelectItem>
                            <SelectItem value="blocked">Zablokowana</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="ghost" onClick={() => deleteOffer(o.offer_id)} className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main Admin Panel
  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <div className="bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-orange-500" />
            <div>
              <h1 className="font-heading font-bold text-2xl text-white">Panel Administratora</h1>
              <p className="text-slate-400">Pełna kontrola nad portalem</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="flex flex-wrap gap-2 bg-transparent">
            <TabsTrigger value="stats" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <TrendingUp className="w-4 h-4 mr-2" />Statystyki
            </TabsTrigger>
            <TabsTrigger value="companies" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Building2 className="w-4 h-4 mr-2" />Firmy
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />Indywidualni
            </TabsTrigger>
            <TabsTrigger value="offers" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />Oferty
            </TabsTrigger>
            <TabsTrigger value="news" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Newspaper className="w-4 h-4 mr-2" />Wiadomości
            </TabsTrigger>
            <TabsTrigger value="chats" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <MessageCircle className="w-4 h-4 mr-2" />Czaty
            </TabsTrigger>
          </TabsList>

          {/* STATS TAB */}
          <TabsContent value="stats">
            {/* Page Visits Stats */}
            <Card className="mb-6 border-2 border-orange-200 bg-orange-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-orange-500" />
                  Statystyki odwiedzin strony
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-white rounded-lg border border-orange-200 shadow-sm">
                    <p className="text-3xl font-bold text-orange-600">{stats?.visits_today || 0}</p>
                    <p className="text-sm text-slate-500">Dzisiaj</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg border border-orange-200 shadow-sm">
                    <p className="text-3xl font-bold text-orange-600">{stats?.visits_week || 0}</p>
                    <p className="text-sm text-slate-500">Ostatnie 7 dni</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg border border-orange-200 shadow-sm">
                    <p className="text-3xl font-bold text-orange-600">{stats?.visits_month || 0}</p>
                    <p className="text-sm text-slate-500">Ostatnie 30 dni</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg border border-orange-200 shadow-sm">
                    <p className="text-3xl font-bold text-orange-600">{stats?.visits_total || 0}</p>
                    <p className="text-sm text-slate-500">Łącznie</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Overview Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Wszyscy użytkownicy" value={stats?.total_users} color="blue" />
              <StatCard icon={Building2} label="Firmy" value={stats?.total_companies} color="purple" />
              <StatCard icon={FileText} label="Wszystkie oferty" value={stats?.total_offers} color="slate" />
              <StatCard icon={Star} label="Opinie" value={stats?.total_reviews} color="yellow" />
            </div>
            
            {/* User Breakdown */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  Podział użytkowników
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{stats?.individual_users || 0}</p>
                    <p className="text-sm text-slate-500">Indywidualni</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{stats?.company_users || 0}</p>
                    <p className="text-sm text-slate-500">Firmy</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{stats?.active_subscriptions || 0}</p>
                    <p className="text-sm text-slate-500">Aktywne subskrypcje</p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{stats?.blocked_users || 0}</p>
                    <p className="text-sm text-slate-500">Zablokowani</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Growth Stats - last 7 and 30 days */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Wzrost portalu
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <p className="text-2xl font-bold text-emerald-600">+{stats?.new_users_week || 0}</p>
                    <p className="text-sm text-slate-500">Nowi użytkownicy (7 dni)</p>
                  </div>
                  <div className="text-center p-4 bg-teal-50 rounded-lg border border-teal-200">
                    <p className="text-2xl font-bold text-teal-600">+{stats?.new_users_month || 0}</p>
                    <p className="text-sm text-slate-500">Nowi użytkownicy (30 dni)</p>
                  </div>
                  <div className="text-center p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                    <p className="text-2xl font-bold text-cyan-600">+{stats?.new_offers_week || 0}</p>
                    <p className="text-sm text-slate-500">Nowe oferty (7 dni)</p>
                  </div>
                  <div className="text-center p-4 bg-sky-50 rounded-lg border border-sky-200">
                    <p className="text-2xl font-bold text-sky-600">+{stats?.new_offers_month || 0}</p>
                    <p className="text-sm text-slate-500">Nowe oferty (30 dni)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Offer Stats */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-orange-500" />
                  Statystyki ofert
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{stats?.active_offers || 0}</p>
                    <p className="text-sm text-slate-500">Aktywne oferty</p>
                  </div>
                  <div className="text-center p-4 bg-slate-100 rounded-lg">
                    <p className="text-2xl font-bold text-slate-600">{stats?.closed_offers || 0}</p>
                    <p className="text-sm text-slate-500">Zamknięte oferty</p>
                  </div>
                  <div className="text-center p-4 bg-indigo-50 rounded-lg">
                    <p className="text-2xl font-bold text-indigo-600">{stats?.offer_responses || 0}</p>
                    <p className="text-sm text-slate-500">Odpowiedzi na oferty</p>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <p className="text-2xl font-bold text-orange-600">{stats?.promoted_companies || 0}</p>
                    <p className="text-sm text-slate-500">Promowane firmy</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Activity & Engagement */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-blue-500" />
                  Aktywność i zaangażowanie
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{stats?.chat_conversations || 0}</p>
                    <p className="text-sm text-slate-500">Konwersacje chat</p>
                  </div>
                  <div className="text-center p-4 bg-violet-50 rounded-lg">
                    <p className="text-2xl font-bold text-violet-600">{stats?.chat_messages || 0}</p>
                    <p className="text-sm text-slate-500">Wiadomości chat</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{stats?.avg_company_rating || 0}</p>
                    <p className="text-sm text-slate-500">Średnia ocena firm</p>
                  </div>
                  <div className="text-center p-4 bg-lime-50 rounded-lg">
                    <p className="text-2xl font-bold text-lime-600">{stats?.companies_with_reviews || 0}</p>
                    <p className="text-sm text-slate-500">Firmy z opiniami</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Revenue Stats */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-500" />
                  Przychody
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{stats?.total_revenue || 0} PLN</p>
                    <p className="text-sm text-slate-500">Łączny przychód</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{stats?.subscription_revenue || 0} PLN</p>
                    <p className="text-sm text-slate-500">Subskrypcje</p>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <p className="text-2xl font-bold text-orange-600">{stats?.promotion_revenue || 0} PLN</p>
                    <p className="text-sm text-slate-500">Promocje</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Content Stats */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Newspaper className="w-5 h-5 text-purple-500" />
                  Treści
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{stats?.news_articles || 0}</p>
                    <p className="text-sm text-slate-500">Artykuły/Wiadomości</p>
                  </div>
                  <div className="text-center p-4 bg-yellow-50 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">{stats?.total_reviews || 0}</p>
                    <p className="text-sm text-slate-500">Opinie użytkowników</p>
                  </div>
                  <div className="text-center p-4 bg-rose-50 rounded-lg">
                    <p className="text-2xl font-bold text-rose-600">{stats?.total_offers || 0}</p>
                    <p className="text-sm text-slate-500">Wszystkie ogłoszenia</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* COMPANIES TAB */}
          <TabsContent value="companies">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <CardTitle>Zarządzanie firmami ({filteredCompanies.length})</CardTitle>
                  <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Szukaj firmy po nazwie lub lokalizacji..." 
                      value={companySearch}
                      onChange={(e) => setCompanySearch(e.target.value)}
                      className="pl-10"
                      data-testid="company-search"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {filteredCompanies.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">
                      {companySearch ? 'Nie znaleziono firm pasujących do wyszukiwania' : 'Brak firm'}
                    </p>
                  ) : (
                    filteredCompanies.map((c) => (
                      <div 
                        key={c.company_id} 
                        className="border rounded-lg p-4 hover:border-orange-300 hover:bg-orange-50/50 transition-colors cursor-pointer"
                        onClick={() => openCompanyDetail(c)}
                        data-testid={`company-row-${c.company_id}`}
                      >
                        <div className="flex items-center gap-4">
                          {c.logo_url ? (
                            <img src={c.logo_url} alt={c.name} className="w-12 h-12 rounded object-cover" />
                          ) : (
                            <div className="w-12 h-12 bg-slate-200 rounded flex items-center justify-center">
                              <Building2 className="w-6 h-6 text-slate-400" />
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-bold">{c.name}</h3>
                              {c.is_promoted && <Badge className="bg-orange-500">Promowana</Badge>}
                            </div>
                            <p className="text-sm text-slate-500">{c.location}</p>
                            <div className="flex items-center gap-4 text-xs text-slate-400 mt-1">
                              <span className="flex items-center gap-1">
                                <Star className="w-3 h-3 text-yellow-500" />{c.rating || 0} ({c.review_count || 0})
                              </span>
                              {c.gallery?.length > 0 && <span>{c.gallery.length} zdjęć</span>}
                            </div>
                          </div>
                          <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4 mr-2" />Szczegóły
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <CardTitle>Użytkownicy indywidualni ({filteredUsers.length})</CardTitle>
                  <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Szukaj użytkownika..." 
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {selectedUser ? (
                  /* USER DETAIL VIEW */
                  <div className="space-y-6">
                    <Button variant="ghost" onClick={closeUserDetail} className="text-slate-600">
                      <ArrowLeft className="w-4 h-4 mr-2" />Powrót do listy
                    </Button>
                    
                    {/* User Info */}
                    <Card className="border-2 border-blue-200">
                      <CardHeader className="bg-blue-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                              {selectedUser.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <h3 className="font-bold text-lg">{selectedUser.name}</h3>
                              <p className="text-sm text-slate-500">{selectedUser.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => openMessageForm(selectedUser)} className="text-blue-600">
                              <MessageCircle className="w-3 h-3 mr-1" />Wiadomość
                            </Button>
                            {selectedUser.is_blocked ? (
                              <Button size="sm" variant="outline" onClick={() => unblockUser(selectedUser.user_id)}>
                                <Unlock className="w-3 h-3 mr-1" />Odblokuj
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => blockUser(selectedUser.user_id, selectedUser.name)} className="text-orange-600">
                                <Ban className="w-3 h-3 mr-1" />Zablokuj
                              </Button>
                            )}
                            <Button size="sm" variant="destructive" onClick={() => deleteUser(selectedUser.user_id)}>
                              <Trash2 className="w-3 h-3 mr-1" />Usuń
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Typ konta</p>
                            <p className="font-medium">Indywidualny</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Status</p>
                            <p className="font-medium">{selectedUser.is_blocked ? <Badge variant="destructive">Zablokowany</Badge> : <Badge className="bg-green-500">Aktywny</Badge>}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Ogłoszenia</p>
                            <p className="font-medium">{userOffers.length}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Wystawione opinie</p>
                            <p className="font-medium">{userReviews.length}</p>
                          </div>
                        </div>
                        {selectedUser.phone && (
                          <div className="mt-4 flex items-center gap-2 text-sm text-slate-600">
                            <Phone className="w-4 h-4" />
                            {selectedUser.phone}
                          </div>
                        )}
                        {selectedUser.location && (
                          <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                            <MapPin className="w-4 h-4" />
                            {selectedUser.location}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* User's Offers */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <FileText className="w-5 h-5 text-orange-500" />
                          Ogłoszenia użytkownika ({userOffers.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {userOffers.length === 0 ? (
                          <p className="text-slate-500 text-sm text-center py-4">Brak ogłoszeń</p>
                        ) : (
                          <div className="space-y-2">
                            {userOffers.map((o) => (
                              <div key={o.offer_id} className="border rounded-lg p-3 hover:bg-slate-50">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium">{o.title}</p>
                                    <p className="text-sm text-slate-500">{o.location} • {o.category}</p>
                                  </div>
                                  <Badge variant={o.status === 'active' ? 'default' : 'secondary'}>{o.status}</Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* User's Reviews */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Star className="w-5 h-5 text-yellow-500" />
                          Opinie wystawione przez użytkownika ({userReviews.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {userReviews.length === 0 ? (
                          <p className="text-slate-500 text-sm text-center py-4">Brak opinii</p>
                        ) : (
                          <div className="space-y-2">
                            {userReviews.map((r) => (
                              <div key={r.review_id} className="border rounded-lg p-3">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <div className="flex">
                                        {[...Array(5)].map((_, i) => (
                                          <Star key={i} className={`w-4 h-4 ${i < r.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200'}`} />
                                        ))}
                                      </div>
                                      <span className="text-sm text-slate-500">{new Date(r.created_at).toLocaleDateString('pl-PL')}</span>
                                    </div>
                                    <p className="text-sm mt-1">{r.comment}</p>
                                  </div>
                                  <Button size="sm" variant="ghost" onClick={() => deleteReview(r.review_id)} className="text-red-500">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  /* USER LIST VIEW */
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {filteredUsers.map((u) => (
                    <div 
                      key={u.user_id} 
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${u.is_blocked ? 'bg-red-50 border-red-200' : 'hover:border-orange-300 hover:bg-orange-50'}`}
                      onClick={() => openUserDetail(u)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold">{u.name}</span>
                            <Badge variant="secondary">Indywidualny</Badge>
                            {u.is_admin && <Badge className="bg-purple-500">Admin</Badge>}
                            {u.is_blocked && <Badge variant="destructive">Zablokowany</Badge>}
                          </div>
                          <p className="text-sm text-slate-500 truncate">{u.email}</p>
                          {u.is_blocked && (
                            <p className="text-xs text-red-600 mt-1">Powód: {u.blocked_reason}</p>
                          )}
                        </div>
                        
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openUserDetail(u); }}>
                          <Eye className="w-3 h-3 mr-1" />Szczegóły
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* OFFERS TAB */}
          <TabsContent value="offers">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <CardTitle>Zarządzanie ofertami ({filteredOffers.length})</CardTitle>
                  <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Szukaj oferty..." 
                      value={offerSearch}
                      onChange={(e) => setOfferSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {filteredOffers.map((o) => (
                    <div key={o.offer_id} className="border rounded-lg p-4 hover:border-slate-300">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold">{o.title}</h3>
                            <Badge variant={o.status === 'active' ? 'default' : o.status === 'blocked' ? 'destructive' : 'secondary'}>
                              {o.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500">{o.location} • {o.category}</p>
                        </div>
                        <div className="flex gap-2">
                          <Select value={o.status} onValueChange={(v) => changeOfferStatus(o.offer_id, v)}>
                            <SelectTrigger className="w-28 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">Aktywna</SelectItem>
                              <SelectItem value="closed">Zamknięta</SelectItem>
                              <SelectItem value="blocked">Zablokowana</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button size="sm" variant="ghost" onClick={() => deleteOffer(o.offer_id)} className="text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* NEWS TAB */}
          <TabsContent value="news">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Wiadomości ({news.length})</CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={generateNews} disabled={generatingNews}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${generatingNews ? 'animate-spin' : ''}`} />
                      {generatingNews ? 'Pobieranie...' : 'Generuj Nowe'}
                    </Button>
                    <Button onClick={() => setShowNewsForm(!showNewsForm)} className="bg-orange-500 hover:bg-orange-600">
                      <Plus className="w-4 h-4 mr-2" />Dodaj
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {showNewsForm && (
                  <div className="mb-6 p-4 border rounded-lg bg-slate-50 space-y-4">
                    <div>
                      <Label>Tytuł</Label>
                      <Input value={newArticle.title} onChange={(e) => setNewArticle({ ...newArticle, title: e.target.value })} />
                    </div>
                    <div>
                      <Label>Treść</Label>
                      <Textarea value={newArticle.summary} onChange={(e) => setNewArticle({ ...newArticle, summary: e.target.value })} rows={3} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Kategoria</Label>
                        <Select value={newArticle.category} onValueChange={(v) => setNewArticle({ ...newArticle, category: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="news">Nowości</SelectItem>
                            <SelectItem value="law">Prawo</SelectItem>
                            <SelectItem value="tips">Porady</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Źródło</Label>
                        <Input value={newArticle.source} onChange={(e) => setNewArticle({ ...newArticle, source: e.target.value })} />
                      </div>
                    </div>
                    <div>
                      <Label>Link do źródła</Label>
                      <Input value={newArticle.source_url} onChange={(e) => setNewArticle({ ...newArticle, source_url: e.target.value })} placeholder="https://..." />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={addNews} className="bg-green-500 hover:bg-green-600">Dodaj</Button>
                      <Button variant="outline" onClick={() => setShowNewsForm(false)}>Anuluj</Button>
                    </div>
                  </div>
                )}
                
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {news.map((article) => (
                    <div key={article.news_id} className="border rounded-lg p-4 hover:border-slate-300">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant={article.category === 'law' ? 'default' : article.category === 'tips' ? 'secondary' : 'outline'}>
                              {article.category === 'news' ? 'Nowości' : article.category === 'law' ? 'Prawo' : 'Porady'}
                            </Badge>
                            <span className="text-xs text-slate-400">• {article.source}</span>
                            {article.source_url && (
                              <a href={article.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-orange-500 flex items-center gap-1">
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                          <h3 className="font-bold mb-1">{article.title}</h3>
                          <p className="text-sm text-slate-600 line-clamp-2">{article.summary}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => deleteNews(article.news_id)} className="text-red-500 ml-4">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CHATS TAB */}
          <TabsContent value="chats">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Historia czatów użytkowników ({chats.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedChat ? (
                  /* CHAT MESSAGES VIEW */
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-4">
                      <Button variant="ghost" onClick={closeChatMessages} className="text-slate-600">
                        <ArrowLeft className="w-4 h-4 mr-2" />Powrót do listy
                      </Button>
                      <div className="text-sm text-slate-500">
                        <span className="font-medium">{selectedChat.participant1?.name || '?'}</span>
                        <span className="mx-2">↔</span>
                        <span className="font-medium">{selectedChat.participant2?.name || '?'}</span>
                      </div>
                    </div>
                    
                    {loadingChat ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                      </div>
                    ) : chatMessages.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <MessageCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p>Brak wiadomości w tej konwersacji</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-[500px] overflow-y-auto p-2">
                        {chatMessages.map((msg) => {
                          const isP1 = msg.sender_id === selectedChat.participant1?.id;
                          return (
                            <div 
                              key={msg.message_id} 
                              className={`flex flex-col ${isP1 ? 'items-start' : 'items-end'}`}
                            >
                              <div className={`max-w-[70%] p-3 rounded-lg ${isP1 ? 'bg-slate-100' : 'bg-blue-100'}`}>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-medium text-slate-700">{msg.sender_name}</span>
                                  <span className="text-xs text-slate-400">
                                    {new Date(msg.created_at).toLocaleString('pl-PL')}
                                  </span>
                                </div>
                                <p className="text-sm text-slate-800">{msg.content}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  /* CHAT LIST VIEW */
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {chats.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <MessageCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p>Brak czatów</p>
                      </div>
                    ) : (
                      chats.map((chat) => (
                        <div 
                          key={chat.conversation_id} 
                          className="border rounded-lg p-4 hover:border-orange-300 hover:bg-orange-50/50 transition-colors cursor-pointer"
                          onClick={() => openChatMessages(chat)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">{chat.participant1?.name || '?'}</span>
                                <Badge variant="outline" className="text-xs">
                                  {chat.participant1?.user_type === 'company' ? 'Firma' : 'Indywidualny'}
                                </Badge>
                                <span className="text-slate-400">↔</span>
                                <span className="font-medium">{chat.participant2?.name || '?'}</span>
                                <Badge variant="outline" className="text-xs">
                                  {chat.participant2?.user_type === 'company' ? 'Firma' : 'Indywidualny'}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 text-xs text-slate-500">
                                <span>{chat.message_count} wiadomości</span>
                                {chat.last_message && (
                                  <span className="truncate max-w-xs">
                                    Ostatnia: {new Date(chat.last_message.created_at).toLocaleDateString('pl-PL')}
                                  </span>
                                )}
                              </div>
                              {chat.last_message && (
                                <p className="text-sm text-slate-600 mt-2 line-clamp-1">
                                  "{chat.last_message.content}"
                                </p>
                              )}
                            </div>
                            <Button variant="outline" size="sm">
                              <Eye className="w-4 h-4 mr-2" />Podgląd
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Modal: Formularz opinii */}
      {showReviewForm && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
          style={{ zIndex: 99999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <Card className="w-full max-w-md bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                {editingReview ? 'Edytuj opinię' : 'Dodaj opinię'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!editingReview && (
                <div>
                  <Label>Firma</Label>
                  <Select value={reviewForm.company_id} onValueChange={(v) => setReviewForm({ ...reviewForm, company_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Wybierz firmę..." /></SelectTrigger>
                    <SelectContent>
                      {companies.map(c => (
                        <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Ocena (gwiazdki)</Label>
                <div className="flex gap-2 mt-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                      className="focus:outline-none"
                    >
                      <Star 
                        className={`w-8 h-8 ${star <= reviewForm.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`} 
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Nazwa użytkownika</Label>
                <Input 
                  value={reviewForm.user_name} 
                  onChange={(e) => setReviewForm({ ...reviewForm, user_name: e.target.value })} 
                  placeholder="np. Jan K."
                />
              </div>
              <div>
                <Label>Komentarz</Label>
                <Textarea 
                  value={reviewForm.comment} 
                  onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} 
                  placeholder="Treść opinii..."
                  rows={3}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={editingReview ? updateReview : createReview} className="bg-green-500 hover:bg-green-600 flex-1">
                  {editingReview ? 'Zapisz zmiany' : 'Dodaj opinię'}
                </Button>
                <Button variant="outline" onClick={() => { setShowReviewForm(false); setEditingReview(null); }}>
                  Anuluj
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Modal: Wyślij wiadomość */}
      {showMessageForm && messageRecipient && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
          style={{ zIndex: 99999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <Card className="w-full max-w-md bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-blue-500" />
                Wyślij wiadomość
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="text-sm text-slate-500">Do:</p>
                <p className="font-medium">{messageRecipient.name || messageRecipient.email}</p>
                <p className="text-xs text-slate-400">{messageRecipient.email}</p>
              </div>
              <div>
                <Label>Treść wiadomości</Label>
                <Textarea 
                  value={messageContent} 
                  onChange={(e) => setMessageContent(e.target.value)} 
                  placeholder="Wpisz treść wiadomości..."
                  rows={4}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={sendAdminMessage} className="bg-blue-500 hover:bg-blue-600 flex-1">
                  Wyślij
                </Button>
                <Button variant="outline" onClick={() => { setShowMessageForm(false); setMessageRecipient(null); }}>
                  Anuluj
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    slate: 'bg-slate-100 text-slate-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600'
  };
  
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colors[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-2xl font-bold">{value || 0}</p>
            <p className="text-sm text-slate-500">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default Admin;
